import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Edit2,
  Wallet,
  Receipt,
  FileText,
  Calendar,
  TrendingUp,
  TrendingDown,
  Users,
  MapPin,
  Trash2,
  PlusCircle,
  Gift,
  ChevronRight,
  Building,
  Hammer,
  DollarSign,
  Shield,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { MyDomain } from "../../../App";
import BossCreateDayWork from "../BossDailyWorkComps/BossCreateDailyWork";
import BossCreateExpenses from "../BossStatisticComps/BossCreateExpenses";
import BossUpdateExpensesModal from "../BossStatisticComps/BossUpdateExpensesModal";
import BossUpdateEmployeeModal from "../BossEmployeeComps/BossUpdateEmployeeModal";
import BossUpdatePlacesModal from "../BossPlacesComps/BossUpdatePlacesModal";
import BossCreateMonthlySalary from "../BossMonthlySalaryComps/BossCreateMonthlySalary";
import BossCreatePlacesGloballyPaidModal from "../BossPlacesComps/BossCreatePlacesGloballyPaidModal";
import BossStatisticPlacesModalAddExtraProfit from "./BossStatisticPlacesModalAddExtraProfit";
import BossCreateInvoice from "../BossInvoicesComps/BossCreateInvoice";

export default function BossStatisticPlacesData() {
  const history = useHistory();

  // ── State from localStorage ──────────────────────────────────────────────
  const [choosedPlace, setChoosedPlace] = useState(null);
  const [choosedMonth, setChoosedMonth] = useState(null);
  const [allData, setAllData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Modal states ─────────────────────────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [existingEmployees, setExistingEmployees] = useState([]);

  const [showCreateExpenseModal, setShowCreateExpenseModal] = useState(false);
  const [showUpdateExpenseModal, setShowUpdateExpenseModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const [showUpdateEmployeeModal, setShowUpdateEmployeeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [showUpdatePlaceModal, setShowUpdatePlaceModal] = useState(false);
  const [showMonthlySalaryModal, setShowMonthlySalaryModal] = useState(false);
  const [showGlobalPaymentModal, setShowGlobalPaymentModal] = useState(false);
  const [showExtraProfitModal, setShowExtraProfitModal] = useState(false);
  const [createInvoiceVisible, setCreateInvoiceVisible] = useState(false);

  // ── Load from localStorage ───────────────────────────────────────────────
  useEffect(() => {
    const place = localStorage.getItem("choosedPlace");
    const month =
      localStorage.getItem("choosedMonth") || moment().format("MM-YYYY");
    const data = localStorage.getItem("allData");
    const user = localStorage.getItem("user");

    if (place) setChoosedPlace(JSON.parse(place));
    if (data) setAllData(JSON.parse(data));
    if (user) setUserData(JSON.parse(user));
    setChoosedMonth(month);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (choosedMonth) localStorage.setItem("choosedMonth", choosedMonth);
  }, [choosedMonth]);

  // ── Fetch all data from API ──────────────────────────────────────────────
  const fetchAllData = async () => {
    if (!userData) return;
    try {
      const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
        UserId: userData.id,
      });
      const data = response.data || {};
      setAllData(data);
      localStorage.setItem("allData", JSON.stringify(data));

      if (choosedPlace?.id && data?.myPlaces) {
        const updatedPlace = data.myPlaces.find(
          (p) => p.id === choosedPlace.id,
        );
        if (updatedPlace) {
          setChoosedPlace(updatedPlace);
          localStorage.setItem("choosedPlace", JSON.stringify(updatedPlace));
        }
      }
    } catch (err) {
      console.error("❌ Error fetching data:", err);
    }
  };

  if (loading || !choosedPlace || !allData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Month list ───────────────────────────────────────────────────────────
  const generateMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const y = d.getFullYear();
      months.push(`${m}-${y}`);
    }
    return months;
  };
  const monthList = generateMonths();

  // ── Expense types ────────────────────────────────────────────────────────
  const expenseTypes = [
    { id: 1, name: "Materials", color: "#3B82F6" },
    { id: 2, name: "Equipment", color: "#8B5CF6" },
    { id: 3, name: "Transportation", color: "#10B981" },
    { id: 4, name: "Utilities", color: "#F59E0B" },
    { id: 5, name: "Other", color: "#6B7280" },
  ];

  // ── Filtered data for SELECTED month ────────────────────────────────────
  const filteredDailyWork = (allData.myDailyWork || []).filter((item) => {
    if (!choosedPlace?.id || !choosedMonth) return false;
    const [, month, year] = item.date.split("-");
    return (
      item.placeId === choosedPlace.id && `${month}-${year}` === choosedMonth
    );
  });

  const filteredExpenses = (allData.myExpenses || []).filter((item) => {
    if (!choosedPlace?.id || !choosedMonth) return false;
    if (item.placeId !== choosedPlace.id) return false;
    if (!item.date || item.date.trim() === "") return false;
    const parts = item.date.split("-");
    if (parts.length !== 3) return false;
    const [, month, year] = parts;
    return `${month}-${year}` === choosedMonth;
  });

  const filteredMonthlySalaries = Array.isArray(allData?.myMonthlySalary)
    ? allData.myMonthlySalary.filter((item) => {
        if (!choosedPlace?.id || !choosedMonth) return false;
        if (item.placeId !== choosedPlace.id) return false;
        if (!item.date || item.date.trim() === "") return false;
        const parts = item.date.split("-");
        if (parts.length !== 3) return false;
        const [, month, year] = parts;
        return `${month}-${year}` === choosedMonth;
      })
    : [];

  const filteredExtraProfits = Array.isArray(allData?.myPlaceExtraProfit)
    ? allData.myPlaceExtraProfit.filter((item) => {
        if (!choosedPlace?.id || !choosedMonth) return false;
        if (item.placeId !== choosedPlace.id) return false;
        if (!item.date || item.date.trim() === "") return false;
        const parts = item.date.split("-");
        if (parts.length !== 3) return false;
        const [, month, year] = parts;
        return `${month}-${year}` === choosedMonth;
      })
    : [];

  // ── All-time data (for global profit paidWay=3) ──────────────────────────
  const allTimeDailyWork = (allData.myDailyWork || []).filter(
    (item) => item.placeId === choosedPlace?.id,
  );
  const allTimeExpenses = (allData.myExpenses || []).filter(
    (item) =>
      item.placeId === choosedPlace.id && item.date && item.date.trim() !== "",
  );
  const allTimeMonthlySalaries = Array.isArray(allData?.myMonthlySalary)
    ? allData.myMonthlySalary.filter(
        (item) =>
          item.placeId === choosedPlace.id &&
          item.date &&
          item.date.trim() !== "",
      )
    : [];

  // ── Totals ───────────────────────────────────────────────────────────────
  const totalExpensesAmount = filteredExpenses.reduce(
    (s, e) => s + (parseFloat(e.price) || 0),
    0,
  );
  const totalDailySalaries = filteredDailyWork.reduce(
    (s, i) => s + (parseFloat(i.salary) || 0),
    0,
  );
  const totalMonthlySalaries = filteredMonthlySalaries.reduce((s, item) => {
    const emp = (allData.myEmployees || []).find(
      (e) => e.id === item.employeeId,
    );
    return s + (emp?.monthlySalary || 0);
  }, 0);
  const totalExtraProfit = filteredExtraProfits.reduce(
    (s, i) => s + (parseFloat(i.profit) || 0),
    0,
  );
  const totalSalaries = totalDailySalaries + totalMonthlySalaries;

  const allTimeExpensesAmount = allTimeExpenses.reduce(
    (s, e) => s + (parseFloat(e.price) || 0),
    0,
  );
  const allTimeDailySalariesTotal = allTimeDailyWork.reduce(
    (s, i) => s + (parseFloat(i.salary) || 0),
    0,
  );
  const allTimeMonthlySalariesTotal = allTimeMonthlySalaries.reduce(
    (s, item) => {
      const emp = (allData.myEmployees || []).find(
        (e) => e.id === item.employeeId,
      );
      return s + (emp?.monthlySalary || 0);
    },
    0,
  );
  const allTimeTotalSalaries =
    allTimeDailySalariesTotal + allTimeMonthlySalariesTotal;

  // Global payments
  const globalPayments = Array.isArray(allData?.myPlacesGloballyPaid)
    ? allData.myPlacesGloballyPaid.filter((p) => p.placeId === choosedPlace?.id)
    : [];
  const totalGlobalPaymentsReceived = globalPayments.reduce(
    (s, p) => s + (parseFloat(p.profit) || 0),
    0,
  );
  const totalGlobalProfit = parseFloat(choosedPlace?.profit || 0);

  // Net profit — matches React Native logic including extraProfit
  let placeRevenue = 0;
  let netProfit = 0;
  if (choosedPlace?.paidWay === 2) {
    placeRevenue = filteredDailyWork.reduce(
      (s, i) => s + (parseFloat(i.placeDailySalary) || 0),
      0,
    );
    netProfit =
      placeRevenue - totalSalaries - totalExpensesAmount + totalExtraProfit;
  } else if (choosedPlace?.paidWay === 3) {
    placeRevenue = totalGlobalPaymentsReceived;
    netProfit =
      totalGlobalPaymentsReceived -
      allTimeTotalSalaries -
      allTimeExpensesAmount +
      totalExtraProfit;
  } else {
    placeRevenue = parseFloat(choosedPlace?.profit || 0);
    netProfit =
      placeRevenue - totalSalaries - totalExpensesAmount + totalExtraProfit;
  }

  // ── Calendar helpers ─────────────────────────────────────────────────────
  const getAllDaysInMonth = () => {
    if (!choosedMonth) return [];
    const [month, year] = choosedMonth.split("-");
    const daysInMonth = moment(`${year}-${month}`, "YYYY-MM").daysInMonth();
    const days = [];
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(`${String(day).padStart(2, "0")}-${month}-${year}`);
    }
    return days;
  };

  const groupByDate = () => {
    const grouped = {};
    filteredDailyWork.forEach((item) => {
      if (!grouped[item.date]) grouped[item.date] = [];
      grouped[item.date].push(item);
    });
    return grouped;
  };

  const allDays = getAllDaysInMonth();
  const groupedData = groupByDate();

  const getDayTotal = (date) =>
    (groupedData[date] || []).reduce(
      (s, i) => s + (parseFloat(i.salary) || 0),
      0,
    );
  const getDayRevenue = (date) =>
    (groupedData[date] || []).reduce(
      (s, i) => s + (parseFloat(i.placeDailySalary) || 0),
      0,
    );
  const hasNotes = (date) =>
    (groupedData[date] || []).some(
      (item) => item.note && item.note.trim() !== "",
    );

  const workingDays = Object.keys(groupedData).length;
  const uniqueEmployees = new Set(filteredDailyWork.map((i) => i.employeeId))
    .size;

  // ── Utility ──────────────────────────────────────────────────────────────
  const getEmployeeName = (id) => {
    const emp = (allData.myEmployees || []).find((e) => e.id === id);
    return emp?.name || `Employee ${id}`;
  };
  const getManagerName = (id) => {
    const mgr = (allData.myEmployees || []).find((e) => e.id === id);
    return mgr?.name || "Me";
  };

  // ── Day click ────────────────────────────────────────────────────────────
  const handleDayClick = (date) => {
    const dayData = groupedData[date] || [];
    const existingEmps = dayData.map((item) => {
      const emp = (allData.myEmployees || []).find(
        (e) => e.id === item.employeeId,
      );
      return {
        id: item.employeeId,
        name: emp?.name || `Employee ${item.employeeId}`,
        salary: item.salary.toString(),
      };
    });
    setSelectedDate(date);
    setExistingEmployees(existingEmps);
    setShowCreateModal(true);
  };

  // ── Delete handlers ──────────────────────────────────────────────────────
  const handleDeleteExpense = async (expense) => {
    if (
      !window.confirm(
        `Are you sure you want to delete expense "${expense.title}"?`,
      )
    )
      return;
    try {
      await axios.delete(`${MyDomain}Expenses/DeleteExpense`, {
        data: { Id: expense.id },
      });
      fetchAllData();
    } catch (error) {
      console.error("❌ Error deleting expense:", error);
      alert("Failed to delete expense");
    }
  };

  const handleDeleteMonthlySalary = async (monthlySalary, employeeName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete monthly salary for ${employeeName}?`,
      )
    )
      return;
    try {
      await axios.delete(`${MyDomain}MonthlySalary/DeleteMonthlySalary`, {
        data: { Id: monthlySalary.id },
      });
      fetchAllData();
    } catch (error) {
      console.error("❌ Error deleting monthly salary:", error);
      alert("Failed to delete monthly salary");
    }
  };

  const handleDeleteGlobalPayment = async (payment) => {
    const date = moment(payment.dateGetPaid, "DD-MM-YYYY").format(
      "MMM DD, YYYY",
    );
    const amount = `₪${parseFloat(payment.profit).toLocaleString()}`;
    if (
      !window.confirm(
        `Are you sure you want to delete payment ${amount} from ${date}?`,
      )
    )
      return;
    try {
      await axios.delete(
        `${MyDomain}PlacesGloballyPaid/DeletePlacesGloballyPaid`,
        { data: { Id: payment.id } },
      );
      fetchAllData();
    } catch (error) {
      console.error("❌ Error deleting global payment:", error);
      alert("Failed to delete payment");
    }
  };

  const handleDeleteExtraProfit = async (extraProfit) => {
    const date = moment(extraProfit.date, "DD-MM-YYYY").format("MMM DD, YYYY");
    const amount = `₪${parseFloat(extraProfit.profit).toLocaleString()}`;
    if (
      !window.confirm(
        `Are you sure you want to delete extra profit ${amount} from ${date}?`,
      )
    )
      return;
    try {
      await axios.delete(`${MyDomain}PlaceExtraProfit/DeletePlaceExtraProfit`, {
        data: { Id: extraProfit.id },
      });
      fetchAllData();
    } catch (error) {
      console.error("❌ Error deleting extra profit:", error);
      alert("Failed to delete extra profit");
    }
  };

  const showFinancialBreakdown =
    placeRevenue > 0 || choosedPlace?.paidWay === 3;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ── */}
      <div className="bg-white px-5 pt-6 pb-4 rounded-b-3xl shadow-md">
        {/* Top row */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => history.goBack()}
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 text-gray-900" />
          </button>

          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
              <MapPin className="w-7 h-7 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">
                {choosedPlace?.place}
              </h1>
              <p className="text-sm text-gray-500">Place Performance</p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-4 gap-2">
          <button
            onClick={() => setShowUpdatePlaceModal(true)}
            className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 transition"
          >
            <Edit2 className="w-4 h-4 text-white" />
            <span className="text-[10px] font-bold text-white">Edit</span>
          </button>
          <button
            onClick={() => setShowMonthlySalaryModal(true)}
            className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 transition"
          >
            <Wallet className="w-4 h-4 text-white" />
            <span className="text-[10px] font-bold text-white">Salary</span>
          </button>
          <button
            onClick={() => setShowCreateExpenseModal(true)}
            className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-yellow-500 hover:bg-yellow-600 transition"
          >
            <Receipt className="w-4 h-4 text-white" />
            <span className="text-[10px] font-bold text-white">Expense</span>
          </button>
          <button
            onClick={() => setCreateInvoiceVisible(true)}
            className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-violet-500 hover:bg-violet-600 transition"
          >
            <FileText className="w-4 h-4 text-white" />
            <span className="text-[10px] font-bold text-white">Invoice</span>
          </button>
        </div>
      </div>

      <div className="px-5 py-5 space-y-6 pb-24">
        {/* ── Month Selector ── */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Select Month</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
            {monthList.map((item) => {
              const [, year] = item.split("-");
              const monthName = moment(item, "MM-YYYY").format("MMM");
              const isSelected = choosedMonth === item;
              return (
                <button
                  key={item}
                  onClick={() => setChoosedMonth(item)}
                  className={`flex-shrink-0 px-5 py-3 rounded-2xl border-2 min-w-[84px] text-center transition-all ${
                    isSelected
                      ? "bg-yellow-400 border-yellow-400 shadow-lg"
                      : "bg-white border-gray-200 hover:border-yellow-300"
                  }`}
                >
                  <p
                    className={`text-sm font-semibold ${isSelected ? "text-white" : "text-gray-900"}`}
                  >
                    {monthName}
                  </p>
                  <p
                    className={`text-xs ${isSelected ? "text-yellow-100" : "text-gray-500"}`}
                  >
                    {year}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Quick Stats ── */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl p-4 shadow-sm border-l-4 border-blue-500 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{workingDays}</p>
              <p className="text-xs text-gray-500 font-medium">Working Days</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border-l-4 border-purple-500 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {uniqueEmployees}
              </p>
              <p className="text-xs text-gray-500 font-medium">Employees</p>
            </div>
          </div>
        </div>

        {/* ── Financial Breakdown ── */}
        {showFinancialBreakdown && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                Financial Breakdown
              </h2>
              <span className="bg-yellow-100 text-yellow-600 text-xs font-semibold px-3 py-1.5 rounded-xl">
                ₪
                {(choosedPlace?.paidWay === 3
                  ? allTimeTotalSalaries + allTimeExpensesAmount
                  : totalSalaries + totalExpensesAmount
                ).toLocaleString()}
              </span>
            </div>

            {/* Revenue / Global Profit card */}
            {(placeRevenue > 0 || choosedPlace?.paidWay === 3) && (
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                  </div>
                  <p className="flex-1 font-semibold text-gray-900 text-sm">
                    {choosedPlace?.paidWay === 2
                      ? "Total Revenue"
                      : choosedPlace?.paidWay === 3
                        ? "Global Profit"
                        : "Monthly Profit"}
                  </p>
                  <p className="font-bold text-green-600 text-base">
                    {choosedPlace?.paidWay === 3
                      ? `₪${totalGlobalPaymentsReceived.toLocaleString()} / ₪${totalGlobalProfit.toLocaleString()}`
                      : `₪${placeRevenue.toLocaleString()}`}
                  </p>
                </div>
                <p className="text-xs text-gray-400 ml-12">
                  {choosedPlace?.paidWay === 2
                    ? "Total daily revenue collected"
                    : choosedPlace?.paidWay === 3
                      ? "Received from total global profit"
                      : "Monthly profit from place"}
                </p>

                {/* Global payments list */}
                {choosedPlace?.paidWay === 3 && (
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-600">
                        Payments Received:
                      </p>
                      <button
                        onClick={() => setShowGlobalPaymentModal(true)}
                        className="flex items-center gap-1 bg-green-50 border border-green-500 text-green-600 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-green-100 transition"
                      >
                        <PlusCircle className="w-3.5 h-3.5" />
                        Add Payment
                      </button>
                    </div>

                    {globalPayments.length === 0 ? (
                      <div className="text-center py-6">
                        <Wallet className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-400 font-medium">
                          No payments yet
                        </p>
                        <p className="text-xs text-gray-300 mt-1 mb-3">
                          Add your first payment
                        </p>
                        <button
                          onClick={() => setShowGlobalPaymentModal(true)}
                          className="flex items-center gap-1 bg-green-50 border border-green-500 text-green-600 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-green-100 transition mx-auto"
                        >
                          <PlusCircle className="w-3.5 h-3.5" />
                          Add Payment
                        </button>
                      </div>
                    ) : (
                      globalPayments.map((payment) => (
                        <div
                          key={payment.id}
                          className="flex items-center gap-2"
                        >
                          <div className="flex-1 flex items-center justify-between bg-green-50 border border-green-100 rounded-xl px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                                <DollarSign className="w-3 h-3 text-green-500" />
                              </div>
                              <p className="text-xs font-medium text-gray-700">
                                {moment(
                                  payment.dateGetPaid,
                                  "DD-MM-YYYY",
                                ).format("MMM DD, YYYY")}
                              </p>
                            </div>
                            <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-lg">
                              ₪{parseFloat(payment.profit).toLocaleString()}
                            </span>
                          </div>
                          <button
                            onClick={() => handleDeleteGlobalPayment(payment)}
                            className="w-8 h-8 bg-red-50 border border-red-100 rounded-full flex items-center justify-center hover:bg-red-100 transition"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Extra Profit card */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-green-500" />
                </div>
                <p className="flex-1 font-semibold text-gray-900 text-sm">
                  Extra Profit
                </p>
                <p className="font-bold text-green-600 text-base">
                  ₪{totalExtraProfit.toLocaleString()}
                </p>
              </div>
              <p className="text-xs text-gray-400 ml-12 mb-4">
                Extra profit from additional work
              </p>

              <div className="pt-3 border-t border-gray-100 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-600">
                    Extra Profits Received:
                  </p>
                  <button
                    onClick={() => setShowExtraProfitModal(true)}
                    className="flex items-center gap-1 bg-green-50 border border-green-500 text-green-600 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-green-100 transition"
                  >
                    <PlusCircle className="w-3.5 h-3.5" />
                    Add Extra
                  </button>
                </div>

                {filteredExtraProfits.length === 0 ? (
                  <div className="text-center py-5">
                    <Gift className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400 font-medium">
                      No extra profits yet
                    </p>
                    <p className="text-xs text-gray-300 mt-1">
                      Add your first extra profit
                    </p>
                  </div>
                ) : (
                  filteredExtraProfits.map((ep) => (
                    <div key={ep.id} className="flex items-center gap-2">
                      <div className="flex-1 flex items-center justify-between bg-green-50 border border-green-100 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <Gift className="w-3.5 h-3.5 text-green-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-700 truncate">
                              {moment(ep.date, "DD-MM-YYYY").format(
                                "MMM DD, YYYY",
                              )}
                            </p>
                            {ep.note && ep.note.trim() !== "" && (
                              <p className="text-[10px] text-gray-500 italic truncate">
                                {ep.note}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-lg ml-2 flex-shrink-0">
                          ₪{parseFloat(ep.profit).toLocaleString()}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDeleteExtraProfit(ep)}
                        className="w-8 h-8 bg-red-50 border border-red-100 rounded-full flex items-center justify-center hover:bg-red-100 transition flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Daily Salaries card */}
            {totalDailySalaries > 0 && (
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-green-500" />
                  </div>
                  <p className="flex-1 font-semibold text-gray-900 text-sm">
                    Total Salaries
                  </p>
                  <p className="font-bold text-red-500 text-base">
                    ₪{totalDailySalaries.toLocaleString()}
                  </p>
                </div>
                <p className="text-xs text-gray-400 ml-12">
                  Total paid to daily employees
                </p>
              </div>
            )}

            {/* Monthly Salaries card */}
            {totalMonthlySalaries > 0 && (
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-purple-500" />
                  </div>
                  <p className="flex-1 font-semibold text-gray-900 text-sm">
                    Monthly Employees
                  </p>
                  <p className="font-bold text-purple-600 text-base">
                    ₪{totalMonthlySalaries.toLocaleString()}
                  </p>
                </div>

                <div className="mt-3 space-y-2">
                  {filteredMonthlySalaries.map((item) => {
                    const emp = (allData.myEmployees || []).find(
                      (e) => e.id === item.employeeId,
                    );
                    if (!emp) return null;
                    return (
                      <div key={item.id} className="flex items-center gap-2">
                        <div className="flex-1 flex items-center justify-between bg-purple-50 border border-purple-100 rounded-xl px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <Users className="w-4 h-4 text-purple-500" />
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-900">
                                {emp.name}
                              </p>
                              <p className="text-[10px] text-gray-400">
                                Paid:{" "}
                                {moment(item.date, "DD-MM-YYYY").format(
                                  "MMM DD",
                                )}
                              </p>
                            </div>
                          </div>
                          <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2.5 py-1 rounded-lg">
                            ₪{emp.monthlySalary?.toLocaleString() || 0}
                          </span>
                        </div>
                        <button
                          onClick={() =>
                            handleDeleteMonthlySalary(item, emp.name)
                          }
                          className="w-8 h-8 bg-red-50 border border-red-100 rounded-full flex items-center justify-center hover:bg-red-100 transition"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Expenses card */}
            {totalExpensesAmount > 0 && (
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-9 h-9 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-yellow-500" />
                  </div>
                  <p className="flex-1 font-semibold text-gray-900 text-sm">
                    Expenses & Invoices
                  </p>
                  <div className="flex items-center gap-1">
                    <p className="font-bold text-yellow-500 text-base">
                      ₪{totalExpensesAmount.toLocaleString()}
                    </p>
                    <ChevronRight className="w-5 h-5 text-yellow-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-400 ml-12 mb-3">
                  View expenses & invoices ({filteredExpenses.length} items)
                </p>

                {/* Expense type breakdown */}
                <div className="space-y-2 mt-1">
                  {expenseTypes.map((type) => {
                    const typeExpenses = filteredExpenses.filter(
                      (exp) => exp.type === type.id,
                    );
                    const typeTotal = typeExpenses.reduce(
                      (s, exp) => s + (parseFloat(exp.price) || 0),
                      0,
                    );
                    if (typeTotal === 0) return null;
                    return (
                      <div
                        key={type.id}
                        className="flex items-center justify-between py-1.5"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center"
                            style={{
                              backgroundColor: `${type.color}22`,
                            }}
                          >
                            <Receipt
                              className="w-3.5 h-3.5"
                              style={{ color: type.color }}
                            />
                          </div>
                          <span className="text-xs font-medium text-gray-700">
                            {type.name}
                          </span>
                          <span className="bg-gray-100 text-gray-500 text-[10px] font-semibold px-2 py-0.5 rounded-lg">
                            {typeExpenses.length}
                          </span>
                        </div>
                        <span
                          className="text-sm font-bold"
                          style={{ color: type.color }}
                        >
                          ₪{typeTotal.toLocaleString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Net Profit card */}
            <div
              className={`rounded-2xl p-4 shadow-sm border-2 ${
                netProfit >= 0
                  ? "bg-green-50 border-green-200"
                  : "bg-red-50 border-red-200"
              }`}
            >
              <div className="flex items-center gap-3 mb-1">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center ${
                    netProfit >= 0 ? "bg-green-100" : "bg-red-100"
                  }`}
                >
                  {netProfit >= 0 ? (
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-red-500" />
                  )}
                </div>
                <p className="flex-1 font-semibold text-gray-900 text-sm">
                  Net Profit
                </p>
                <p
                  className={`text-xl font-extrabold ${
                    netProfit >= 0 ? "text-green-600" : "text-red-500"
                  }`}
                >
                  ₪{netProfit.toLocaleString()}
                </p>
              </div>
              <p className="text-xs text-gray-400 ml-12">
                {choosedPlace?.paidWay === 2
                  ? "Revenue minus costs and expenses"
                  : choosedPlace?.paidWay === 3
                    ? "Received profit minus all costs and expenses"
                    : "Profit minus costs and expenses"}
              </p>
            </div>
          </div>
        )}

        {/* ── Daily Operations Grid ── */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">
              Daily Operations
            </h2>
            <span className="bg-yellow-100 text-yellow-600 text-xs font-semibold px-3 py-1.5 rounded-xl">
              {allDays.length} days
            </span>
          </div>

          {allDays.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-12 h-12 text-gray-300" />
              </div>
              <h3 className="text-base font-semibold text-gray-600 mb-2">
                No Data This Month
              </h3>
              <p className="text-sm text-gray-400">Select a different month</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {allDays.map((date) => {
                const dayData = groupedData[date] || [];
                const hasWork = dayData.length > 0;
                const hasNote = hasNotes(date);
                const dayNumber = moment(date, "DD-MM-YYYY").format("DD");
                const dayName = moment(date, "DD-MM-YYYY").format("ddd");
                const isToday = moment(date, "DD-MM-YYYY").isSame(
                  moment(),
                  "day",
                );

                return (
                  <button
                    key={date}
                    onClick={() => handleDayClick(date)}
                    className={`relative text-left rounded-xl p-2.5 min-h-[150px] border-2 transition-all ${
                      hasWork
                        ? "bg-yellow-50 border-yellow-200 hover:shadow-md"
                        : "bg-white border-gray-100 hover:border-gray-200"
                    } ${isToday ? "ring-2 ring-blue-400" : ""}`}
                  >
                    {/* Note indicator */}
                    {hasNote && (
                      <div className="absolute top-1 right-1 w-4 h-4 bg-yellow-100 rounded-full flex items-center justify-center z-10">
                        <FileText className="w-2.5 h-2.5 text-yellow-500" />
                      </div>
                    )}

                    {/* Day header */}
                    <div className="flex flex-col items-center pb-2 mb-2 border-b border-gray-100">
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center mb-1 ${
                          hasWork ? "bg-yellow-100" : "bg-gray-100"
                        }`}
                      >
                        <span
                          className={`text-sm font-bold ${
                            hasWork
                              ? "text-yellow-500"
                              : isToday
                                ? "text-blue-500"
                                : "text-gray-500"
                          }`}
                        >
                          {dayNumber}
                        </span>
                      </div>
                      <span className="text-[9px] font-semibold text-gray-400 uppercase">
                        {dayName}
                      </span>
                    </div>

                    {/* Content */}
                    {hasWork ? (
                      <div className="space-y-1.5">
                        {/* Badges row */}
                        <div className="flex gap-1 flex-wrap">
                          <span className="flex items-center gap-0.5 bg-purple-100 text-purple-600 text-[9px] font-bold px-1.5 py-0.5 rounded">
                            <Users className="w-2.5 h-2.5" />
                            {dayData.length}
                          </span>
                          {choosedPlace?.paidWay === 2 ? (
                            <span className="flex items-center gap-0.5 bg-green-100 text-green-600 text-[9px] font-bold px-1.5 py-0.5 rounded">
                              <TrendingUp className="w-2.5 h-2.5" />₪
                              {getDayRevenue(date).toFixed(0)}
                            </span>
                          ) : (
                            <span className="flex items-center gap-0.5 bg-red-100 text-red-500 text-[9px] font-bold px-1.5 py-0.5 rounded">
                              <DollarSign className="w-2.5 h-2.5" />₪
                              {getDayTotal(date).toFixed(0)}
                            </span>
                          )}
                        </div>

                        {/* Employee list */}
                        <div className="space-y-1">
                          {dayData.map((item) => (
                            <div
                              key={item.id}
                              className="bg-white rounded-lg px-2 py-1.5"
                            >
                              <div className="flex items-center gap-1.5">
                                <div className="w-4 h-4 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                                  <Users className="w-2.5 h-2.5 text-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[9px] font-semibold text-gray-900 truncate">
                                    {getEmployeeName(item.employeeId)}
                                  </p>
                                  <p className="text-[9px] font-bold text-red-400">
                                    ₪{item.salary}
                                  </p>
                                  {choosedPlace?.paidWay === 2 &&
                                    item.placeDailySalary && (
                                      <p className="text-[9px] font-semibold text-green-500">
                                        Rev: ₪{item.placeDailySalary}
                                      </p>
                                    )}
                                  {item.whoMake && (
                                    <p className="text-[8px] text-gray-400 italic">
                                      by {getManagerName(item.whoMake)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-3">
                        <PlusCircle className="w-7 h-7 text-gray-200 mb-1" />
                        <p className="text-[9px] text-gray-300 font-medium">
                          No activity
                        </p>
                        <p className="text-[8px] text-gray-300">Tap to add</p>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      <BossCreateDayWork
        visible={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setExistingEmployees([]);
        }}
        onSuccess={() => {
          setShowCreateModal(false);
          setExistingEmployees([]);
          fetchAllData();
        }}
        places={allData.myPlaces}
        employees={allData.myEmployees}
        preselectedDate={selectedDate}
        preselectedPlaceId={choosedPlace?.id}
        preselectedEmployees={existingEmployees}
      />

      <BossCreateExpenses
        visible={showCreateExpenseModal}
        onClose={() => setShowCreateExpenseModal(false)}
        onSuccess={() => {
          setShowCreateExpenseModal(false);
          fetchAllData();
        }}
        preselectedPlaceId={choosedPlace?.id}
      />

      <BossUpdateExpensesModal
        visible={showUpdateExpenseModal}
        onClose={() => setShowUpdateExpenseModal(false)}
        expense={selectedExpense}
        onUpdateSuccess={() => {
          setShowUpdateExpenseModal(false);
          fetchAllData();
        }}
      />

      <BossUpdateEmployeeModal
        visible={showUpdateEmployeeModal}
        onClose={() => setShowUpdateEmployeeModal(false)}
        employee={selectedEmployee}
        onUpdateSuccess={() => {
          setShowUpdateEmployeeModal(false);
          fetchAllData();
        }}
      />

      <BossUpdatePlacesModal
        visible={showUpdatePlaceModal}
        onClose={() => setShowUpdatePlaceModal(false)}
        place={choosedPlace}
        onUpdateSuccess={async () => {
          setShowUpdatePlaceModal(false);
          await fetchAllData();
        }}
      />

      <BossCreateMonthlySalary
        visible={showMonthlySalaryModal}
        onClose={() => setShowMonthlySalaryModal(false)}
        onSuccess={() => {
          setShowMonthlySalaryModal(false);
          fetchAllData();
        }}
        placeId={choosedPlace?.id}
        placeName={choosedPlace?.place}
      />

      <BossCreatePlacesGloballyPaidModal
        visible={showGlobalPaymentModal}
        onClose={() => setShowGlobalPaymentModal(false)}
        onSuccess={() => {
          setShowGlobalPaymentModal(false);
          fetchAllData();
        }}
        placeId={choosedPlace?.id}
        placeName={choosedPlace?.place}
      />

      <BossStatisticPlacesModalAddExtraProfit
        visible={showExtraProfitModal}
        onClose={() => setShowExtraProfitModal(false)}
        onSuccess={() => {
          setShowExtraProfitModal(false);
          fetchAllData();
        }}
        placeId={choosedPlace?.id}
        placeName={choosedPlace?.place}
      />

      <BossCreateInvoice
        visible={createInvoiceVisible}
        onClose={() => setCreateInvoiceVisible(false)}
        onSuccess={() => {
          setCreateInvoiceVisible(false);
          fetchAllData();
        }}
      />

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
